export interface VillaBookingCalendarProps {
  year: number;
  month: number; // 0-11
  bookedDates: Set<string>; // occupied nights, YYYY-MM-DD
  checkIn: string | null;
  checkOut: string | null;
  today: string;
  weekdays: string[];
  months: string[];
  prevMonthLabel: string;
  nextMonthLabel: string;
  legendAvailable: string;
  legendBooked: string;
  legendSelected: string;
  onSelectDate: (dateStr: string) => void;
  onChangeMonth: (delta: number) => void;
}

function getCalendarCells(year: number, month: number): (number | null)[] {
  const firstWeekday = new Date(year, month, 1).getDay();
  // Convert from Sunday=0 to Monday=0
  const mondayFirstWeekday = (firstWeekday + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < mondayFirstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function VillaBookingCalendar(props: VillaBookingCalendarProps) {
  const {
    year,
    month,
    bookedDates,
    checkIn,
    checkOut,
    today,
    weekdays,
    months,
    prevMonthLabel,
    nextMonthLabel,
    legendAvailable,
    legendBooked,
    legendSelected,
    onSelectDate,
    onChangeMonth,
  } = props;

  const cells = getCalendarCells(year, month);
  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => onChangeMonth(-1)}
          aria-label={prevMonthLabel}
          className="w-10 h-10 rounded-md flex items-center justify-center text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <span className="font-heading text-lg font-semibold text-foreground-950">
          {months[month]} {year}
        </span>
        <button
          type="button"
          onClick={() => onChangeMonth(1)}
          aria-label={nextMonthLabel}
          className="w-10 h-10 rounded-md flex items-center justify-center text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((wd, i) => (
          <div
            key={i}
            className="text-center text-[11px] md:text-xs font-semibold text-foreground-500 py-2"
          >
            {wd}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day === null) {
            return <div key={i} className="h-11 md:h-12" />;
          }
          const dateStr = `${monthPrefix}-${String(day).padStart(2, '0')}`;
          const isPast = dateStr < today;
          const isBooked = bookedDates.has(dateStr);
          const isCheckIn = checkIn === dateStr;
          const isCheckOut = checkOut === dateStr;
          const isInRange =
            checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;

          const disabled = isPast || isBooked;

          let cellClass = 'bg-background-50 text-foreground-900 hover:bg-background-100 cursor-pointer';
          if (isPast) cellClass = 'text-foreground-300 cursor-default';
          else if (isBooked) cellClass = 'bg-accent-100/70 text-accent-400 cursor-not-allowed';
          else if (isCheckIn || isCheckOut)
            cellClass = 'bg-primary-500 text-white cursor-pointer';
          else if (isInRange) cellClass = 'bg-primary-100 text-primary-900 cursor-pointer';

          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => onSelectDate(dateStr)}
              className={`h-11 md:h-12 rounded-md text-sm flex flex-col items-center justify-center transition-colors ${cellClass}`}
            >
              <span className={`leading-none ${isBooked ? 'line-through' : ''}`}>{day}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-5">
        <span className="inline-flex items-center gap-2 text-xs text-foreground-600">
          <span className="w-3.5 h-3.5 rounded bg-background-50 border border-background-300" />
          {legendAvailable}
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-foreground-600">
          <span className="w-3.5 h-3.5 rounded bg-primary-500" />
          {legendSelected}
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-foreground-600">
          <span className="w-3.5 h-3.5 rounded bg-accent-100 border border-accent-200" />
          {legendBooked}
        </span>
      </div>
    </div>
  );
}